// The session domain: pure types, a total fold, and selectors over the folded state.
//
// This module is the ONLY place a session event is interpreted. The aggregate definition in
// aggregates.ts delegates every reducer here rather than restating the arithmetic, because two
// copies of "arrival takes the court mean" is two chances to round differently, and a device that
// rounds differently silently diverges from every other device in the hall (docs/sync.md).
//
// Nothing here reads a clock or a random source. Every device folds the same event stream and must
// land on the same state.

export type Court = 'white' | 'red' | 'purple' | 'gold';

// Canonical order, white < red < purple < gold. Promotion moves a player RIGHT in this array, so a
// higher index is a higher division and `COURTS.indexOf` is what every up/down test compares.
export const COURTS = ['white', 'red', 'purple', 'gold'] as const satisfies readonly Court[];

// A positive whole number. Either the reducer assigned it from fold order, or the front desk typed
// it off the club's pre-allocation spreadsheet (docs/roster.md). Never computed by a device against
// state it read, which is what keeps an OCC retry's identical payload safe to re-send.
export type PlayerNumber = number;

export type SessionStatus = 'scheduled' | 'running' | 'stopped';

// Where a player's number comes from. `preassigned` is the club typing numbers into a spreadsheet
// from the website sign-ups and the desk reading one off a printout; `sequential` is the desk
// tapping a court and being handed the next free number (docs/roster.md).
//
// The mode is a property of the SESSION and every desk in the hall reads the same one, which is why
// it is folded state and not a device setting. The FOLD does not branch on it: `playerRegistered`
// carries a number or it does not, and the reducer honours what it is given. The mode is what the
// desk SCREEN reads to decide whether to ask a human for one. A fold that branched on a value the
// admin can change mid-session would replay the same stream two ways.
export type NumberingMode = 'preassigned' | 'sequential';

// The union at runtime, for the same reason ATTRIBUTES exists below: the type says two names, a
// peer's event says whatever it says.
const NUMBERING_MODES: ReadonlySet<string> = new Set(['preassigned', 'sequential']);

// The two coach flags (docs/scoring.md). Both persist for the whole session and travel with the
// player across courts, which is why they live on the player record and not on the court.
export type Attribute = 'big' | 'lightbulb';

// The union, at runtime. `setAttribute` checks against it: the type says 'big' | 'lightbulb', a
// peer's event says whatever it says.
const ATTRIBUTES: ReadonlySet<string> = new Set(['big', 'lightbulb']);

// How an arrival onto a court is scored. `mean` is the everyday rule (docs/roster.md,
// docs/scoring.md). `bottom` and `top` exist for the 60-minute tick, where nobody plays afterwards
// and a player promoted at the whistle has not earned a mid-table slot in their new division
// (docs/rotation-algorithm.md, "The 60-minute tick").
export type Placement = 'mean' | 'bottom' | 'top';

// One row of a tick's move list: who moves, off which court, onto which (docs/rotation.md).
export interface MoveRow {
  readonly player: PlayerNumber;
  readonly from: Court;
  readonly to: Court;
}

export interface MoveList {
  readonly tick: number;
  readonly moves: readonly MoveRow[];
}

// The tagged union the reducer folds. Payloads match the event catalogue in docs/sync.md.
// `sessionScheduled` is deliberately absent: it lives on the PROJECT aggregate.
export type SessionEvent =
  // The payload carries the court, a client-generated correlation id, and OPTIONALLY the number
  // the desk typed. With no number the reducer assigns the smallest free one, so an OCC retry that
  // re-sends the identical payload is safe. WITH a number the payload is already fixed, so the
  // retry is safe for the same reason from the other direction: the number was never computed
  // against state, it was read off a spreadsheet.
  | {
      kind: 'playerRegistered';
      payload: { court: Court; correlationId: string; number?: PlayerNumber };
    }
  // Admin, and settable at any point in the session's life. Changing it renumbers nobody: the
  // players already registered keep the numbers they were given.
  | { kind: 'numberingModeSet'; payload: { mode: NumberingMode } }
  // The admin device stamps its own clock here, so ticks run relative to the ACTUAL start
  // (docs/session-lifecycle.md). OPTIONAL, because P1 shipped `sessionStarted {}` and a device on
  // that build still writes one. The fold keeps the FIRST value it is ever given.
  | { kind: 'sessionStarted'; payload: { startedAtMs?: number } }
  | { kind: 'sessionStopped'; payload: Record<string, never> }
  // Weights are docs/scoring.md: up +3, down -2, court-local.
  | { kind: 'voteCast'; payload: { player: PlayerNumber; direction: 'up' | 'down' } }
  // Carries the resulting value, never a flip instruction: two coaches tapping the same flag in
  // the same moment must not cancel into a no-op (docs/scoring.md).
  | {
      kind: 'attributeSet';
      payload: { player: PlayerNumber; attribute: Attribute; value: boolean };
    }
  // The outlier constant, tunable on the day. An event, never a local setting, or the tick stops
  // being reproducible (docs/rotation-algorithm.md).
  | { kind: 'thresholdSet'; payload: { sigma: number } }
  // The rebalance tolerance, the second knob on the admin screen and tunable for the same reason.
  // How far the fullest court may exceed the emptiest before a headcount move fires
  // (docs/rotation.md, "Balance").
  | { kind: 'rebalanceToleranceSet'; payload: { tolerance: number } }
  | { kind: 'playerStruckOff'; payload: { player: PlayerNumber } }
  | { kind: 'playerReinstated'; payload: { player: PlayerNumber; court: Court } }
  // The mover's tick-off. Ticking the row is what moves the player (docs/rotation.md).
  | { kind: 'playerMoved'; payload: { player: PlayerNumber; toCourt: Court; placement: Placement } }
  | { kind: 'moveListComputed'; payload: { tick: number; moves: readonly MoveRow[] } };

export interface PlayerRecord {
  readonly number: PlayerNumber;
  // The court they are on now. Kept across a strike-off so a reinstatement onto the SAME court can
  // tell it apart from a reinstatement onto a different one (docs/roster.md).
  readonly court: Court;
  readonly score: number;
  readonly struckOff: boolean;
  readonly big: boolean;
  readonly lightbulb: boolean;
  // When this player arrived on the court they are on now, as a position in the event stream and
  // never as a wall time. The rebalance breaks a score tie on "longest on this court", and
  // docs/rotation-algorithm.md is explicit that the ordering must come from the stream's own
  // ordering, not from the clock of whichever machine is asking. Bumped on registration and on any
  // arrival onto a DIFFERENT court; a reinstatement onto the same court resumes, so it keeps the
  // sequence it had, exactly as it keeps its score.
  readonly arrivalSeq: number;
  // How many UP-votes this player has collected, ever. The net score above is court-local and a
  // move resets it; this is session-global and travels with the player, because the two numbers
  // answer different questions and the CSV ships both (docs/reporting.md, "Columns"). Down-votes
  // move the score and never this. A vote that the fold no-ops — an unknown player, a struck-off
  // one — is a no-op here too.
  readonly upvotes: number;
}

// Pre-allocated is the default (session-v2/goal.md): the club decided to number off its sign-up
// spreadsheet, so a session nobody configured is one of those. Admin-tunable at runtime through
// `numberingModeSet`.
export const DEFAULT_NUMBERING: NumberingMode = 'preassigned';

// Scout's starting value. Admin-tunable at runtime through `thresholdSet`, and not sacred.
export const DEFAULT_SIGMA = 2.5;

// The scout's other starting value (goal.md): tolerance 3 and 4 scored the same on placement
// accuracy and let the courts drift further, so 2 is the pick. Admin-tunable through
// `rebalanceToleranceSet`.
export const DEFAULT_TOLERANCE = 2;

// Read through the selectors below rather than field by field: the selectors are what the roster,
// the tick and the report agree on, and they are what the oracle pins.
export interface SessionState {
  readonly status: SessionStatus;
  // Sorted ascending by `number`, whatever order the registrations arrived in, and NOT dense: a
  // pre-allocated day registers 7 before 3 and may never see 1 at all. `players[n - 1]` is
  // therefore not player n; go through `playerRecord(state, n)`. Sorting on insert rather than on
  // read is what keeps two devices holding identical arrays for the same set of players, and what
  // keeps `activePlayers` in number order for the report.
  readonly players: readonly PlayerRecord[];
  // correlationId -> the number that registration was assigned. The front desk device reads its
  // own number back out of here (docs/sync.md, "the version itself does not identify the number").
  readonly correlations: Readonly<Record<string, PlayerNumber>>;
  // Where the NEXT registration's number comes from. Defaults to `preassigned`: the club
  // pre-allocates on a spreadsheet, and a session whose admin never touched the setting is one of
  // those days.
  readonly numbering: NumberingMode;
  // How many times the mode has actually CHANGED. A monotonic marker, not a copy of the mode, and
  // that is the whole point: a device holding something typed under one mode needs to know the
  // ground moved, and comparing modes cannot tell "never changed" from "changed away and back".
  // The front desk's typed number is the holder (src/views/frontdesk/FrontDeskStore.ts), and it has
  // no other way to find out: `reaction` and `autorun` are banned in app code (INVARIANTS rule 3),
  // so there is nothing for it to subscribe to except the fold. Same reason `arrivals` is kept in
  // state rather than derived.
  readonly numberingEpoch: number;
  readonly sigma: number;
  readonly tolerance: number;
  readonly moveLists: readonly MoveList[];
  // The admin device's clock at the ACTUAL start, or undefined for a session nobody has started
  // and for one started by a build that stamped no time. Every tick's due-ness is measured from
  // here (docs/session-lifecycle.md, "The clock").
  readonly startedAtMs: number | undefined;
  // How many arrivals this session has seen. The counter behind `PlayerRecord.arrivalSeq`, kept in
  // state rather than derived so that "longest on this court" survives a player moving twice.
  readonly arrivals: number;
}

export function emptySessionState(): SessionState {
  return {
    status: 'scheduled',
    players: [],
    correlations: {},
    numbering: DEFAULT_NUMBERING,
    numberingEpoch: 0,
    sigma: DEFAULT_SIGMA,
    tolerance: DEFAULT_TOLERANCE,
    moveLists: [],
    startedAtMs: undefined,
    arrivals: 0,
  };
}

// Pure, total, deterministic. An unknown or ill-ordered event is a no-op, never a throw and never
// a corrupt state: a device that cannot fold a peer's event is a device that has left the session.
export function foldSessionEvent(state: SessionState, event: SessionEvent): SessionState {
  switch (event.kind) {
    case 'playerRegistered':
      return register(
        state,
        event.payload.court,
        event.payload.correlationId,
        event.payload.number,
      );
    case 'numberingModeSet':
      return setNumbering(state, event.payload.mode);
    case 'sessionStarted':
      return start(state, event.payload.startedAtMs);
    case 'sessionStopped':
      return state.status === 'running' ? { ...state, status: 'stopped' } : state;
    case 'voteCast':
      return vote(state, event.payload.player, event.payload.direction);
    case 'attributeSet':
      return setAttribute(
        state,
        event.payload.player,
        event.payload.attribute,
        event.payload.value,
      );
    case 'thresholdSet':
      return setThreshold(state, event.payload.sigma);
    case 'rebalanceToleranceSet':
      return setTolerance(state, event.payload.tolerance);
    case 'playerStruckOff':
      return strikeOff(state, event.payload.player);
    case 'playerReinstated':
      return reinstate(state, event.payload.player, event.payload.court);
    case 'playerMoved':
      return move(state, event.payload.player, event.payload.toCourt, event.payload.placement);
    case 'moveListComputed':
      return recordMoveList(state, event.payload.tick, event.payload.moves);
    // An event kind from a build newer than this one. Ignore it rather than throw: a fold that
    // dies on a peer's event takes the whole screen down mid-session.
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------------------------
// Reducers. One per event kind, each total and each returning `state` unchanged when the event
// does not apply. The aggregate definition calls foldSessionEvent, never these directly.
// ---------------------------------------------------------------------------------------------

// The FIRST start time wins and every later one is ignored, which is what makes a stop and a
// restart harmless: "the clock does not pause. A stop and restart does not shift the schedule"
// (docs/session-lifecycle.md). A device on the P1 build stamps nothing, so a session can be running
// with no start time at all; the engine treats that as "no schedule to compute" rather than
// guessing.
function start(state: SessionState, startedAtMs: number | undefined): SessionState {
  const stamped =
    state.startedAtMs ??
    (typeof startedAtMs === 'number' && Number.isFinite(startedAtMs) ? startedAtMs : undefined);
  if (state.status === 'running' && stamped === state.startedAtMs) return state;
  return { ...state, status: 'running', startedAtMs: stamped };
}

function register(
  state: SessionState,
  court: Court,
  correlationId: string,
  requested: PlayerNumber | undefined,
): SessionState {
  // The same correlation id folded twice is the same registration, not a second player. Dedup is
  // the SDK's job on the write path; this covers the read path, where a catch-up can re-deliver.
  if (hasCorrelation(state, correlationId)) return state;
  // A number off the spreadsheet, or the smallest one nobody holds. `nextFreeNumber` is not
  // `players.length + 1`: a pre-allocated day leaves gaps, and counting the array would hand out a
  // number somebody already has.
  const number = requested ?? nextFreeNumber(state);
  // Refused, and NO correlation recorded. The correlation map is how the desk reads its own number
  // back, so recording one here would answer "your registration landed" for a registration that did
  // not happen, and a human would walk off with a number belonging to somebody else. A duplicate is
  // impossible by construction on a pre-allocated day (session-v2/goal.md), which is exactly why a
  // duplicate arriving anyway is a mistake to refuse rather than a case to absorb. The aggregate's
  // precondition is the real gate; this keeps the fold's own "never a corrupt state" claim true.
  if (!isPlayerNumber(number) || indexOfPlayer(state, number) >= 0) return state;
  const arrivals = state.arrivals + 1;
  const arrival: PlayerRecord = {
    number,
    court,
    // Every arrival, first or late, takes the destination court's current mean rounded up. Zero
    // would drop a late arrival to the bottom of a court that has been accruing for half an hour.
    score: courtMean(state, court),
    struckOff: false,
    big: false,
    lightbulb: false,
    arrivalSeq: arrivals,
    upvotes: 0,
  };
  return {
    ...state,
    // Sorted in, not appended: `players` is ordered by number and a pre-allocated day registers 7
    // before 3.
    players: insertByNumber(state.players, arrival),
    arrivals,
    // Computed key, so a correlationId of `__proto__` becomes an own property rather than moving
    // the prototype. Reads go through hasCorrelation for the same reason.
    correlations: { ...state.correlations, [correlationId]: number },
  };
}

// The mode the desk screen reads. A name outside the union is ignored, for the same reason
// `setAttribute` checks: the type constrains this build, not a peer's event.
function setNumbering(state: SessionState, mode: NumberingMode): SessionState {
  if (!NUMBERING_MODES.has(mode)) return state;
  // An event that sets the mode it already holds changes nothing, epoch included: it is a re-
  // delivery or a second admin device agreeing, not a change anybody has to react to.
  if (state.numbering === mode) return state;
  // `?? 0` here AND in the selector below. This fold is documented total and never-corrupting, and
  // `undefined + 1` is NaN, which then compares unequal to itself forever: the front desk's draft
  // guard would read an empty box for the rest of the session and the court buttons would never
  // arm, with nothing on screen saying why. The undefined is not hypothetical input, it is the SDK
  // rehydration boundary. The engine reuses a persisted state document whenever `schemaVersion` has
  // not moved, and a document written before this field existed simply has no key. Do not delete
  // this as unreachable.
  return { ...state, numbering: mode, numberingEpoch: (state.numberingEpoch ?? 0) + 1 };
}

function vote(state: SessionState, player: PlayerNumber, direction: 'up' | 'down'): SessionState {
  const record = recordOf(state, player);
  // A player who is not on the floor collects no votes (docs/roster.md): the coach who could have
  // tapped them cannot see them, so a vote that lands here is a stale tap racing the strike-off.
  if (record === undefined || record.struckOff) return state;
  // One event moves both numbers, so they can never disagree about the same vote: the score by the
  // weight (docs/scoring.md), the counter only on the way up.
  return replace(state, {
    ...record,
    score: record.score + (direction === 'up' ? 3 : -2),
    upvotes: record.upvotes + (direction === 'up' ? 1 : 0),
  });
}

function setAttribute(
  state: SessionState,
  player: PlayerNumber,
  attribute: Attribute,
  value: boolean,
): SessionState {
  // An attribute name outside the union spreads a field the record does not have, and a name of
  // `number` would rewrite the record's own identity. The ingress schema is the real gate; this
  // keeps the fold's "never a corrupt state" claim true on its own terms.
  if (!ATTRIBUTES.has(attribute)) return state;
  const record = recordOf(state, player);
  if (record === undefined) return state;
  if (record[attribute] === value) return state;
  return replace(state, { ...record, [attribute]: value });
}

function setThreshold(state: SessionState, sigma: number): SessionState {
  // A non-positive or non-finite sigma makes the outlier test meaningless. The write-time
  // precondition rejects it; the fold stays total and ignores it.
  if (!Number.isFinite(sigma) || sigma <= 0) return state;
  return state.sigma === sigma ? state : { ...state, sigma };
}

// A whole number of players, and at least one. The tolerance is compared against a headcount
// difference, so a fractional or negative one is a knob that cannot mean anything — and ZERO is a
// knob that means something impossible: "exact equality is neither achievable nor wanted"
// (docs/rotation.md, "Balance"), because a hall of 103 across four courts can never be level, and a
// tick asked for it would move every player it legally could, every ten minutes, forever.
function setTolerance(state: SessionState, tolerance: number): SessionState {
  if (!Number.isInteger(tolerance) || tolerance < 1) return state;
  return state.tolerance === tolerance ? state : { ...state, tolerance };
}

function strikeOff(state: SessionState, player: PlayerNumber): SessionState {
  const record = recordOf(state, player);
  if (record === undefined || record.struckOff) return state;
  return replace(state, { ...record, struckOff: true });
}

function reinstate(state: SessionState, player: PlayerNumber, court: Court): SessionState {
  const record = recordOf(state, player);
  if (record === undefined || !record.struckOff) return state;
  // Same court, same score, same tenure: twenty minutes on the sideline is not a reason to lose the
  // hour's work. A different court is an arrival like any other, and arriving on a court is
  // arriving on a court (docs/roster.md).
  if (court === record.court) return replace(state, { ...record, struckOff: false });
  return arrive(state, { ...record, struckOff: false }, court, courtMean(state, court));
}

function move(
  state: SessionState,
  player: PlayerNumber,
  toCourt: Court,
  placement: Placement,
): SessionState {
  const record = recordOf(state, player);
  if (record === undefined || record.struckOff) return state;
  if (record.court === toCourt) return state;
  return arrive(state, record, toCourt, arrivalScore(state, toCourt, placement));
}

// One arrival onto a court that is not the one the player was on: a new score and a new place in
// the arrival order, so the next tick's "longest on this court" tiebreak counts their time on the
// court they are on NOW and not their time in the session.
function arrive(
  state: SessionState,
  record: PlayerRecord,
  court: Court,
  score: number,
): SessionState {
  const arrivals = state.arrivals + 1;
  const next = replace(state, { ...record, court, score, arrivalSeq: arrivals });
  // `replace` refuses a record whose number is not an existing slot. If it refused, the counter
  // must not move either: nothing arrived.
  return next === state ? state : { ...next, arrivals };
}

function recordMoveList(
  state: SessionState,
  tick: number,
  moves: readonly MoveRow[],
): SessionState {
  // Exactly one list per tick, immutable from the moment it appears, so the mover's list never
  // shifts under their thumb (docs/rotation.md). The write-time precondition is the real guard;
  // this makes a re-delivered event harmless.
  if (state.moveLists.some((list) => list.tick === tick)) return state;
  return {
    ...state,
    moveLists: [...state.moveLists, { tick, moves: moves.map((row) => ({ ...row })) }],
  };
}

// ---------------------------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------------------------

export function playerNumberForCorrelation(
  state: SessionState,
  correlationId: string,
): PlayerNumber | undefined {
  return hasCorrelation(state, correlationId) ? state.correlations[correlationId] : undefined;
}

// Active players only, ascending: struck-off players leave the roster (docs/roster.md).
export function courtRoster(state: SessionState, court: Court): PlayerNumber[] {
  return activeOn(state, court).map((record) => record.number);
}

export function courtCounts(state: SessionState): Record<Court, number> {
  const counts: Record<Court, number> = { white: 0, red: 0, purple: 0, gold: 0 };
  for (const record of state.players) if (!record.struckOff) counts[record.court]++;
  return counts;
}

// Ceiling-rounded, so a mean of -2.5 is -2. An empty court is 0. Every device rounds the same way
// or their derived state diverges (docs/scoring.md).
export function courtMean(state: SessionState, court: Court): number {
  const on = activeOn(state, court);
  if (on.length === 0) return 0;
  return ceilInt(on.reduce((sum, record) => sum + record.score, 0) / on.length);
}

// Total for an unknown player: a selector that throws takes a coach screen down mid-session.
export function playerScore(state: SessionState, player: PlayerNumber): number {
  return recordOf(state, player)?.score ?? 0;
}

// Undefined for a player who does not exist and for one who has been struck off: they are not on
// a court. The court they left is remembered internally, for reinstatement, and nowhere else.
export function playerCourt(state: SessionState, player: PlayerNumber): Court | undefined {
  const record = recordOf(state, player);
  return record === undefined || record.struckOff ? undefined : record.court;
}

export function sessionStatus(state: SessionState): SessionStatus {
  return state.status;
}

export function sessionSigma(state: SessionState): number {
  return state.sigma;
}

// Where the next registration's number comes from. The front desk screen reads this to decide
// whether to ask a human to type one (docs/roster.md).
export function sessionNumbering(state: SessionState): NumberingMode {
  return state.numbering;
}

// How many times the mode has changed. A device that captured this alongside something the mode
// makes meaningful can tell that the ground moved, including when it moved away and back.
export function sessionNumberingEpoch(state: SessionState): number {
  // Coalesced for the same reason the reducer is: a rehydrated document from before this field
  // existed has no key, and a reader returning undefined would compare unequal to a draft captured
  // a moment earlier on the same device.
  return state.numberingEpoch ?? 0;
}

// Whether any player already holds this number, struck off or not. A struck-off player does NOT
// release their number: it belongs to the human the club allocated it to on a spreadsheet, and
// their going home does not hand it to somebody else. The front desk reads this to refuse a
// duplicate before it writes one.
export function isNumberTaken(state: SessionState, number: PlayerNumber): boolean {
  return indexOfPlayer(state, number) >= 0;
}

// The record for a number, or undefined. Every caller outside this module goes through here rather
// than `state.players[n - 1]`: the array is sorted by number, not indexed by it.
export function playerRecord(state: SessionState, number: PlayerNumber): PlayerRecord | undefined {
  return recordOf(state, number);
}

// Whether this correlation id already has a registration in state. The aggregate's precondition
// reads it: the front desk's ladder re-sends the identical payload after a lost ack, and a
// duplicate-number guard without this clause would refuse exactly the retry the ladder exists to
// survive (docs/sync.md, the lost-ack path).
export function hasRegistration(state: SessionState, correlationId: string): boolean {
  return hasCorrelation(state, correlationId);
}

// Whether a registration carrying this number may be written. Undefined is always free: the reducer
// will assign one. Exported because the aggregate's precondition must ask the same question the
// fold does, and two copies of it is one chance to disagree.
export function registrationNumberFree(
  state: SessionState,
  number: PlayerNumber | undefined,
): boolean {
  if (number === undefined) return true;
  return isPlayerNumber(number) && !isNumberTaken(state, number);
}

// The rebalance knob, defaulted for a session where the admin never touched it.
export function sessionTolerance(state: SessionState): number {
  return state.tolerance;
}

// The admin device's clock at the actual start, or undefined when nobody stamped one. Every tick's
// due-ness is measured from here and never from a device's memory of pressing Start: a laptop that
// opened the app at minute 35 has no memory to consult (docs/session-lifecycle.md).
export function sessionStartedAtMs(state: SessionState): number | undefined {
  return state.startedAtMs;
}

// Flags survive a strike-off and travel with the player across courts (docs/scoring.md).
export function playerAttributes(
  state: SessionState,
  player: PlayerNumber,
): { big: boolean; lightbulb: boolean } {
  const record = recordOf(state, player);
  return { big: record?.big ?? false, lightbulb: record?.lightbulb ?? false };
}

export function moveListForTick(state: SessionState, tick: number): MoveList | undefined {
  return state.moveLists.find((list) => list.tick === tick);
}

// The list the mover is walking: the newest one, by tick number rather than by arrival order, so a
// re-derived missed tick landing after a later one cannot send the hall backwards. A screen still
// showing tick 1 at minute 25 sends people to courts the last tick already moved them off.
export function latestMoveList(state: SessionState): MoveList | undefined {
  let latest: MoveList | undefined;
  for (const list of state.moveLists) {
    if (latest === undefined || list.tick > latest.tick) latest = list;
  }
  return latest;
}

// Players who have gone home, in number order. The mover's reinstate picker reads this; nothing
// else does, because a struck-off player is off the roster everywhere else (docs/roster.md).
export function struckOffPlayers(state: SessionState): readonly PlayerRecord[] {
  return state.players.filter((record) => record.struckOff);
}

// Every player still on the floor, in number order. The mover's audit view and the report read
// this; both exclude struck-off players.
export function activePlayers(state: SessionState): readonly PlayerRecord[] {
  return state.players.filter((record) => !record.struckOff);
}

// An aggregate nothing has ever been written to, as opposed to one that ran. The SDK folds a
// never-created aggregate to the empty state rather than throwing (`catchUpLocked` swallows
// `NotExist`), so a caller that reads a session cannot tell "this day never happened" from "this
// day happened and nobody registered" by the fold's shape alone. These three fields are what
// separates them: the first event of any real session stamps a start or bumps `arrivals`, and a
// player struck off in the last minute still leaves a record behind. The report is the caller that
// needs the difference (docs/reporting.md, "Getting the file").
export function sessionUntouched(state: SessionState): boolean {
  return state.arrivals === 0 && state.players.length === 0 && state.startedAtMs === undefined;
}

// ---------------------------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------------------------

function recordOf(state: SessionState, player: PlayerNumber): PlayerRecord | undefined {
  const index = indexOfPlayer(state, player);
  return index < 0 ? undefined : state.players[index];
}

// A positive whole number. Zero, a negative, a fraction, NaN and Infinity are not numbers anybody
// writes on an arm.
function isPlayerNumber(value: unknown): value is PlayerNumber {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

// Where this number sits in `players`, or -1. The fast path is the sequentially numbered day, where
// the array IS dense and 1-based and this is the index lookup it always was; the scan is the
// pre-allocated one, where the numbers have gaps. A linear scan over a hall of a hundred costs
// nothing, and the alternative is a second index in state for every device to keep in step.
function indexOfPlayer(state: SessionState, player: PlayerNumber): number {
  if (!isPlayerNumber(player)) return -1;
  const dense = state.players[player - 1];
  if (dense !== undefined && dense.number === player) return player - 1;
  return state.players.findIndex((record) => record.number === player);
}

// The smallest positive integer no player holds. `players` is sorted ascending and numbers are
// unique, so the first slot the sequence skips is the answer.
function nextFreeNumber(state: SessionState): PlayerNumber {
  let candidate = 1;
  for (const record of state.players) {
    if (record.number > candidate) break;
    if (record.number === candidate) candidate++;
  }
  return candidate;
}

// Insert keeping the ascending order. The caller has already established the number is free.
function insertByNumber(
  players: readonly PlayerRecord[],
  arrival: PlayerRecord,
): readonly PlayerRecord[] {
  const at = players.findIndex((record) => record.number > arrival.number);
  if (at < 0) return [...players, arrival];
  return [...players.slice(0, at), arrival, ...players.slice(at)];
}

function activeOn(state: SessionState, court: Court): PlayerRecord[] {
  return state.players.filter((record) => !record.struckOff && record.court === court);
}

function replace(state: SessionState, record: PlayerRecord): SessionState {
  // Only a number some player already holds. A record for a number nobody holds is not an update,
  // it is a registration nobody wrote, and letting it through would put a player on a court without
  // an arrival, a correlation or a place in the numbering.
  const index = indexOfPlayer(state, record.number);
  if (index < 0) return state;
  const players = state.players.slice();
  players[index] = record;
  return { ...state, players };
}

function arrivalScore(state: SessionState, court: Court, placement: Placement): number {
  const on = activeOn(state, court);
  if (on.length === 0) return 0;
  const scores = on.map((record) => record.score);
  if (placement === 'bottom') return Math.min(...scores) - 1;
  if (placement === 'top') return Math.max(...scores) + 1;
  return courtMean(state, court);
}

// Math.ceil(-0.4) is -0, and -0 is not 0 under Object.is, so an assertion or a serialisation
// downstream would see a value that prints as 0 and does not compare as one.
function ceilInt(value: number): number {
  const rounded = Math.ceil(value);
  return rounded === 0 ? 0 : rounded;
}

// Own-property lookup: a correlationId of `constructor` or `toString` would otherwise resolve to
// an inherited member and read as an already-seen registration.
function hasCorrelation(state: SessionState, correlationId: string): boolean {
  return Object.prototype.hasOwnProperty.call(state.correlations, correlationId);
}
